import type { SeatTier, WorkspaceSeatTier } from "@/shared/seat-tiers.ts";
import { $api } from "@/utils/fetch.ts";

/**
 * A row from `GET /api/members`. The org-role + SCIM fields are OPTIONAL because
 * the endpoint field-strips them for non-admin callers (H1) — only admin surfaces
 * (the members table, the edit-member dialog) can rely on them being present.
 */
/** Mirrors the backend `MemberStatus` union; see `api/util/active-users.ts`. */
export type MemberStatus =
	| "active"
	| "invited"
	| "deactivated"
	| "idp_disabled";

export interface OrganizationMember {
	userId: string;
	email: string;
	isAdmin?: boolean;
	hasJoined: boolean;
	hasSso: boolean;
	scimProvisionedAt?: string;
	scimActive?: boolean;
	/**
	 * Whether the member can use oneAI, and if not, which blocker would remain after
	 * the others were cleared. Derived server-side by `deriveMemberStatus` so the badge
	 * cannot disagree with the predicate that actually excludes people — the roster used
	 * to compute this from `hasJoined || hasSso` and reported deactivated members as
	 * active.
	 */
	status?: MemberStatus;
	deactivatedAt?: string;
	/** The member's seat type. Admin-only, like the fields above. */
	seatTier?: SeatTier;
	/** The type a scheduled seat change makes them at the next renewal. */
	seatTierFromRenewal?: WorkspaceSeatTier;
}

export interface OrganizationMembersResponse {
	members: OrganizationMember[];
	totalCount: number;
}

export interface OrganizationMembersStats {
	totalUsers: number;
	adminUsers: number;
	regularUsers: number;
	pendingInvites: number;
}

// Organization Members Queries and Mutations
export const organizationMembersQueryOptions = (
	page?: number,
	pageSize?: number,
	search?: string,
) =>
	$api.queryOptions("get", "/api/members", {
		params: {
			query: {
				page,
				pageSize,
				search,
			},
		},
	});

export const organizationMembersStatsQueryOptions = () =>
	$api.queryOptions("get", "/api/members/stats");

export const useAddOrganizationMemberMutation = () =>
	$api.useMutation("post", "/api/members");

export const useUpdateOrganizationMemberMutation = () =>
	$api.useMutation("put", "/api/members/{userId}");

export const useRemoveOrganizationMemberMutation = () =>
	$api.useMutation("delete", "/api/members/{userId}");

/** Change a member's seat type: up takes effect now, down at the next renewal. */
export const useChangeMemberSeatTypeMutation = () =>
	$api.useMutation("post", "/api/members/{userId}/seat-type");

export const useCancelMemberSeatTypeChangeMutation = () =>
	$api.useMutation("post", "/api/members/{userId}/seat-type/cancel");

/**
 * Deactivate (`active: false`) or reactivate (`active: true`) a member.
 *
 * Reversible and non-destructive: it keeps their spaces, data, passkeys and connected
 * integrations, which is what separates it from a removal. The backend refuses to
 * deactivate the last active administrator, and reactivation can fail on the seat cap —
 * both arrive as a message the caller must surface rather than replace.
 */
export const useSetMemberStatusMutation = () =>
	$api.useMutation("put", "/api/members/{userId}/status");

/**
 * Hand a member's shared spaces and projects to another member.
 *
 * Additive: the recipient becomes an administrator of everything the departing member
 * created or administered, and the departing member keeps their own access until it is
 * revoked separately. Personal spaces are never touched. The recipient must be an active
 * member of the same org — the backend refuses otherwise.
 */
export const useTransferMemberOwnershipMutation = () =>
	$api.useMutation("post", "/api/members/{userId}/transfer");

export const useResetMemberCredentialsMutation = () =>
	$api.useMutation("post", "/api/members/{userId}/reset");

export const useResendMemberInviteMutation = () =>
	$api.useMutation("post", "/api/members/{userId}/reinvite");

/**
 * Replace which login methods a member may use — the SSO providers, plus the
 * email flow as a method of its own.
 *
 * The body is the COMPLETE desired set, not a delta, so a caller must send the
 * methods it is keeping alongside the one it is changing. Passkeys are not part
 * of it: they are a credential the member registered, available to every member
 * including SSO ones, and nothing here touches them.
 *
 * The backend refuses a set that would leave the member with no way in at all —
 * no provider, no email login and no passkey — as a message the caller must
 * surface rather than replace.
 */
export const useSetMemberLoginMethodsMutation = () =>
	$api.useMutation("put", "/api/members/{userId}/login-methods");

/**
 * One member in full, for the admin detail surface. Separate from the roster row: the
 * list is field-stripped for non-admin callers, this endpoint is admin-only and carries
 * account standing — who switched them off, and when.
 */
export const memberDetailQueryOptions = (userId: string) =>
	$api.queryOptions("get", "/api/members/{userId}", {
		params: { path: { userId } },
	});

/**
 * What one member holds. `kind` splits projects from everything else, matching the two
 * tabs; the target's OWN rights come back on each row, not the caller's, and their
 * personal space and project are never included.
 */
export const memberSpacesQueryOptions = (
	userId: string,
	options: {
		kind: "spaces" | "projects";
		page?: number;
		pageSize?: number;
		search?: string;
	},
) =>
	$api.queryOptions("get", "/api/members/{userId}/spaces", {
		params: {
			path: { userId },
			query: {
				kind: options.kind,
				page: options.page,
				pageSize: options.pageSize,
				search: options.search,
			},
		},
	});
